import type { Difficulty, MCQQuestion } from './types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

type QuestionType = 'compare' | 'add' | 'sub' | 'identify';

/**
 * N-3-9：同分母分數大小比較與加減（結果不超過 2）。
 * difficulty 1：分母 2-4，分子 1-分母
 * difficulty 2：分母 3-8，分子 1-分母，含加減
 * difficulty 3：分母 4-12，加減結果可達 1 又幾分之幾
 */
export function generateFracCompare(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  const denomMax = difficulty === 1 ? 4 : difficulty === 2 ? 8 : 12;
  const denomMin = difficulty === 1 ? 2 : 3;
  const denominator = randInt(rand, denomMin, denomMax);

  // Question type selection based on difficulty
  const qTypes: QuestionType[] = difficulty === 1
    ? ['compare', 'identify', 'compare']
    : difficulty === 2
    ? ['compare', 'add', 'sub', 'identify']
    : ['add', 'sub', 'compare', 'add'];

  const qType = qTypes[Math.floor(rand() * qTypes.length)];

  let prompt: string;
  let correct: string;
  let options: string[];
  let explanation: string;

  if (qType === 'compare') {
    // ⚠ 2026-06-11 根治死迴圈:分母 2 的同分母真分數只有 1/2 一種,要抽出兩個
    // 不同分子的 while 永遠跳不出。比大小題型分母至少 3。
    const den = Math.max(denominator, 3);
    const numA = randInt(rand, 1, den - 1);
    let numB = randInt(rand, 1, den - 1);
    while (numB === numA) numB = randInt(rand, 1, den - 1);

    const fracA = `${numA}/${den}`;
    const fracB = `${numB}/${den}`;
    const bigger = numA > numB ? fracA : fracB;
    const smaller = numA < numB ? fracA : fracB;

    prompt = `${fracA} 和 ${fracB}，哪個比較大？`;
    correct = bigger;
    options = [bigger, smaller, `兩個一樣大`, `${den}/${den}`];
    explanation = `分母相同時，分子越大分數越大。${numA} ${numA > numB ? '>' : '<'} ${numB}，所以 ${bigger} > ${smaller}`;

  } else if (qType === 'add') {
    const numA = randInt(rand, 1, Math.max(1, denominator - 2));
    const numB = randInt(rand, 1, denominator - numA);
    const sumNum = numA + numB;

    // Format: if sum ≥ denominator, show as mixed number
    let correctStr: string;
    if (sumNum === denominator) {
      correctStr = '1';
    } else if (sumNum > denominator) {
      const whole = Math.floor(sumNum / denominator);
      const rem = sumNum % denominator;
      correctStr = rem === 0 ? String(whole) : `${whole}又${rem}/${denominator}`;
    } else {
      correctStr = `${sumNum}/${denominator}`;
    }

    prompt = `${numA}/${denominator} + ${numB}/${denominator} = ?`;
    correct = correctStr;

    const wrongNum1 = sumNum + 1;
    const wrongNum2 = sumNum - 1 > 0 ? sumNum - 1 : sumNum + 2;
    const wrongStr1 = wrongNum1 >= denominator
      ? wrongNum1 === denominator ? '1' : `${Math.floor(wrongNum1 / denominator)}又${wrongNum1 % denominator}/${denominator}`
      : `${wrongNum1}/${denominator}`;
    const wrongStr2 = wrongNum2 > 0
      ? wrongNum2 >= denominator ? `1` : `${wrongNum2}/${denominator}`
      : `1/${denominator}`;
    const wrongStr3 = `${numA + numB}/${denominator + denominator}`; // classic wrong: add denominators

    options = [correct, wrongStr1, wrongStr2, wrongStr3];
    explanation = `同分母相加，分母不變，分子相加：${numA}+${numB}=${sumNum}，結果為 ${correctStr}`;

  } else if (qType === 'sub') {
    const numA = randInt(rand, 2, denominator);
    const numB = randInt(rand, 1, numA - 1);
    const diffNum = numA - numB;

    let correctStr: string;
    if (numA === denominator) {
      // 1 - numB/denominator
      correctStr = diffNum === 0 ? '0' : `${diffNum}/${denominator}`;
    } else {
      correctStr = diffNum === 0 ? '0' : `${diffNum}/${denominator}`;
    }

    const fracA = numA === denominator ? '1' : `${numA}/${denominator}`;
    prompt = `${fracA} − ${numB}/${denominator} = ?`;
    correct = correctStr;

    const wrongNum1 = diffNum + 1;
    const wrongNum2 = diffNum > 1 ? diffNum - 1 : diffNum + 2;
    const wrongStr1 = wrongNum1 === 0 ? '0' : `${wrongNum1}/${denominator}`;
    const wrongStr2 = `${wrongNum2}/${denominator}`;
    const wrongStr3Fixed = `${numA}/${denominator}`; // forgot to subtract

    options = [correct, wrongStr1, wrongStr2, wrongStr3Fixed];
    explanation = `同分母相減，分母不變，分子相減：${numA}−${numB}=${diffNum}，結果為 ${correctStr}`;

  } else {
    // identify: which fraction is less than 1
    const numA = randInt(rand, 1, denominator - 1);
    const fracA = `${numA}/${denominator}`;
    prompt = `下列哪個分數小於 1？`;
    correct = fracA;
    const whole = '1';
    const greaterFrac = `${denominator + randInt(rand, 1, 2)}/${denominator}`;
    const equalFrac = `${denominator}/${denominator}`;
    options = [correct, whole, greaterFrac, equalFrac];
    explanation = `分子 (${numA}) 小於分母 (${denominator}) 的分數小於 1。${fracA} 的分子小於分母，所以 ${fracA} < 1`;
  }

  // Ensure 4 unique options
  const seen = new Set<string>();
  const uniqueOptions: string[] = [];
  for (const o of options) {
    if (!seen.has(o)) { seen.add(o); uniqueOptions.push(o); }
  }
  while (uniqueOptions.length < 4) {
    const extra = `${randInt(rand, 1, denominator - 1)}/${denominator}`;
    if (!seen.has(extra) && extra !== correct) { seen.add(extra); uniqueOptions.push(extra); }
    else if (!seen.has('0')) { seen.add('0'); uniqueOptions.push('0'); }
    else uniqueOptions.push(`${denominator + uniqueOptions.length}/${denominator}`);
  }

  // Deterministic shuffle
  const shuffled = uniqueOptions.slice(0, 4).sort((x, y) => {
    const h = (s: string) => s.split('').reduce((a, c) => a + c.charCodeAt(0), seed);
    return mulberry32(h(x))() - mulberry32(h(y))();
  });

  return {
    kind: 'mcq',
    prompt,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation,
    kp: 'math.fraction.compare',
  };
}
